import { DashboardHeader } from '@/components/dashboard/dashboard-header'
import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useTRPC } from '@/trpc/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Check, X } from 'lucide-react'
import { DateTime } from 'luxon'
import { useState } from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute('/dashboard/supervisor/leaves/')({
  component: RouteComponent,
})

function formatDate(iso: string): string {
  return DateTime.fromISO(iso).toFormat('MMM d, yyyy')
}

function RouteComponent() {
  const api = useTRPC()
  const queryClient = useQueryClient()

  const [employeeFilter, setEmployeeFilter] = useState('')

  const { data: requests, isPending: isPendingRequests } = useQuery({
    ...api.leave.getPendingLeaveRequests.queryOptions(),
  })

  const updateStatusMutation = useMutation({
    ...api.leave.updateLeaveRequestStatus.mutationOptions({
      onSuccess: (_data, variables) => {
        toast.success(
          variables.status === 'approved' ? 'Leave request approved' : 'Leave request rejected',
        )
        queryClient.invalidateQueries({
          queryKey: api.leave.getPendingLeaveRequests.queryKey(),
        })
      },
      onError: (error) => {
        toast.error('Failed to update leave request', { description: error.message })
      },
    }),
  })

  const filteredRequests = (requests ?? []).filter((request) => {
    const employeeName = `${request.employee.firstName} ${request.employee.lastName}`
    return employeeName.toLowerCase().includes(employeeFilter.trim().toLowerCase())
  })

  function handleApprove(leaveRequestId: string) {
    updateStatusMutation.mutate({ leaveRequestId, status: 'approved' })
  }

  function handleReject(leaveRequestId: string) {
    updateStatusMutation.mutate({ leaveRequestId, status: 'rejected' })
  }

  return (
    <DashboardShell>
      <DashboardHeader
        heading="Leave Approvals"
        text="Review and act on pending leave requests."
      />

      <Card className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
        <CardContent className="pt-6">
          <div className="mb-4">
            <Input
              placeholder="Filter by employee name..."
              value={employeeFilter}
              onChange={(event) => setEmployeeFilter(event.target.value)}
              className="max-w-xs"
            />
          </div>

          {isPendingRequests ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Leave Type</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead>Half-day</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground">
                      No pending leave requests.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRequests.map((request) => (
                    <TableRow key={request.leaveRequestId}>
                      <TableCell className="font-medium">
                        {request.employee.firstName} {request.employee.lastName}
                      </TableCell>
                      <TableCell>{request.leaveType.name}</TableCell>
                      <TableCell>
                        {request.isHalfDay
                          ? formatDate(request.startDate)
                          : `${formatDate(request.startDate)} - ${formatDate(request.endDate)}`}
                      </TableCell>
                      <TableCell>
                        {request.isHalfDay ? <Badge variant="outline">Half-day</Badge> : 'No'}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {request.reason || <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-emerald-700 dark:text-emerald-400"
                            disabled={updateStatusMutation.isPending}
                            onClick={() => handleApprove(request.leaveRequestId)}
                          >
                            <Check className="mr-1 h-3.5 w-3.5" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-700 dark:text-red-400"
                            disabled={updateStatusMutation.isPending}
                            onClick={() => handleReject(request.leaveRequestId)}
                          >
                            <X className="mr-1 h-3.5 w-3.5" />
                            Reject
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </DashboardShell>
  )
}
